import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { Observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Importer } from '@feathr/blackbox';
import type { ITanStackColumnMeta } from '@feathr/components';
import { DateTimeCell, LinkNameCell, NumberCell, TanStackHeader } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import ImporterStateChip from './ImporterStateChip';
import optionsColumn from './optionsColumn';

type TCountField = 'rows' | 'inserted' | 'duplicates' | 'skipped';

function countColumn(id: TCountField, title: string, tooltip: string): ColumnDef<Importer> {
  return {
    accessorFn: (row): number => row.get(id) ?? 0,
    cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
      <Observer>{(): JSX.Element => <NumberCell value={row.original.get(id)} />}</Observer>
    ),
    header: (): JSX.Element => <TanStackHeader title={title} tooltip={tooltip} />,
    id,
    meta: { align: 'right' } satisfies ITanStackColumnMeta,
    sortDescFirst: false,
  };
}

/**
 * Column definitions for the `/data/imports` table.
 *
 * Each column `id` is the field the list route orders by, so a renamed id stops sorting. Sorting
 * is server-side; the `accessorFn`s only make TanStack treat a column as sortable.
 *
 * Every cell reads the model through `Observer`: `TanStackTable` invokes these callbacks inside
 * its own body, so the page's `observer` does not track them.
 */
export default function dataImportsColumns(t: TFunction): Array<ColumnDef<Importer>> {
  return [
    {
      accessorFn: (row): string => row.name,
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <Observer>
          {function useAnonymousFunction(): JSX.Element {
            const localUrl = useLocalUrl();

            return (
              <LinkNameCell label={row.original.name} to={localUrl(row.original.getItemUrl())} />
            );
          }}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Import')} />,
      id: 'name',
      meta: { isFlex: true, isPrimary: true } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): string => row.get('state'),
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <ImporterStateChip importer={row.original} />
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Status')} />,
      id: 'state',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      accessorFn: (row): boolean => row.isOptOut,
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => (
            <>{row.original.isOptOut ? t('Opt-out Import') : t('Person Import')}</>
          )}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Type')} />,
      id: 'overrides',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      sortDescFirst: false,
    },
    {
      // Created time stamp is extracted from id as defined in MongoDB
      accessorFn: (row): string => row.id,
      cell: ({ row }: CellContext<Importer, unknown>): JSX.Element => (
        <Observer>
          {(): JSX.Element => <DateTimeCell value={row.original.dateCreated.toISOString()} />}
        </Observer>
      ),
      header: (): JSX.Element => <TanStackHeader title={t('Created')} />,
      id: 'id',
      meta: { align: 'center' } satisfies ITanStackColumnMeta,
      // The table opens sorted newest-first, so the first click flips to oldest-first.
      sortDescFirst: true,
    },
    countColumn('rows', t('Total Records'), t('Number of records found in the uploaded file.')),
    countColumn(
      'inserted',
      t('Persons Created'),
      t('New contacts that were created from this import.'),
    ),
    countColumn(
      'duplicates',
      t('Persons Updated'),
      t('Number of existing contacts that were matched and updated.'),
    ),
    countColumn(
      'skipped',
      t('Persons Skipped'),
      t(
        'Number of records that were not imported, because of a missing or improperly formatted email address, or because the import failed before reaching them.',
      ),
    ),
    optionsColumn(),
  ];
}
